import React from 'react';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FilterButton, StyledActionButton, StyledSegmentedControl, StyledSegmentButton } from './SectionBar.styled';

interface E53StatusFiltersProps {
  customFilters: {
    serviceTypes?: string[];
    mailFormats?: string[];
    accountingTypes?: string[];
    fromPeriod?: string;
    toPeriod?: string;
    onServiceTypesChange?: (values: string[]) => void;
    onMailFormatsChange?: (values: string[]) => void;
    onAccountingTypesChange?: (values: string[]) => void;
    onFromPeriodChange?: (period: string) => void;
    onToPeriodChange?: (period: string) => void;
    onExport?: (format: 'HTML' | 'PDF' | 'XLS') => void;
    isExporting?: boolean;
    // E53 Status specific filters
    e53StatusPpo?: string;
    e53StatusYear?: string;
    e53StatusDirection?: string;
    e53StatusStatus?: string;
    e53StatusPpoOptions?: Array<{ id: string; name: string; disabled: boolean }>;
    e53StatusYearOptions?: string[];
    e53StatusIsPpoLoading?: boolean;
    onE53StatusPpoChange?: (value: string) => void;
    onE53StatusYearChange?: (value: string) => void;
    onE53StatusDirectionChange?: (value: string) => void;
    onE53StatusStatusChange?: (value: string) => void;
    onE53StatusSubmit?: () => void;
    onE53StatusReset?: () => void;
    e53StatusIsSubmitting?: boolean;
    // Cascading options from parent SectionBar
    yearQuarterOptions?: Array<{ value: string; label: string; year: string; quarter: string }>;
  };
}

const E53StatusFilters: React.FC<E53StatusFiltersProps> = ({ customFilters }) => {
  // State for dropdown menus
  const [serviceTypesAnchorEl, setServiceTypesAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mailFormatsAnchorEl, setMailFormatsAnchorEl] = React.useState<null | HTMLElement>(null);
  const [accountingTypesAnchorEl, setAccountingTypesAnchorEl] = React.useState<null | HTMLElement>(null);
  const [fromPeriodAnchorEl, setFromPeriodAnchorEl] = React.useState<null | HTMLElement>(null);
  const [toPeriodAnchorEl, setToPeriodAnchorEl] = React.useState<null | HTMLElement>(null);
  const [e53StatusPpoAnchorEl, setE53StatusPpoAnchorEl] = React.useState<null | HTMLElement>(null);
  const [e53StatusYearAnchorEl, setE53StatusYearAnchorEl] = React.useState<null | HTMLElement>(null);

  const isServiceTypesMenuOpen = Boolean(serviceTypesAnchorEl);
  const isMailFormatsMenuOpen = Boolean(mailFormatsAnchorEl);
  const isAccountingTypesMenuOpen = Boolean(accountingTypesAnchorEl);
  const isFromPeriodMenuOpen = Boolean(fromPeriodAnchorEl);
  const isToPeriodMenuOpen = Boolean(toPeriodAnchorEl);
  const isE53StatusPpoMenuOpen = Boolean(e53StatusPpoAnchorEl);
  const isE53StatusYearMenuOpen = Boolean(e53StatusYearAnchorEl);

  // Generate year-quarter options for E53StatusFilters (from 2008 to current year)
  // Use cascaded options from parent if provided, otherwise generate all options
  const yearQuarterOptions = React.useMemo(() => {
    // If parent provides cascaded options, use them
    if (customFilters.yearQuarterOptions && customFilters.yearQuarterOptions.length > 0) {
      return customFilters.yearQuarterOptions;
    }
    
    // Otherwise, generate all options
    const options: { value: string; label: string; year: string; quarter: string }[] = [];
    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    const currentYear = new Date().getFullYear();
    
    for (let year = currentYear; year >= 2008; year--) { // Reverse order to show recent years first
      quarters.forEach(quarter => {
        const value = `${year}-${quarter}`;
        const label = `${year} ${quarter}`;
        options.push({
          value,
          label,
          year: year.toString(),
          quarter
        });
      });
    }
    
    return options;
  }, [customFilters.yearQuarterOptions]);

  // Filter options
  const serviceTypeOptions = [
    { id: 'A', label: 'A - Airmail Priority' },
    { id: 'B', label: 'B - SAL Non Priority' },
    { id: 'C', label: 'C - Surface Non-Priority' },
    { id: 'D', label: 'D - Surface Priority' },
  ];

  const mailFormatOptions = [
    { id: 'P', label: 'P - Letters' },
    { id: 'Q', label: 'Q - Flats' },
    { id: 'R', label: 'R - Packets' },
    { id: 'X', label: 'X - PDF Manual' },
    { id: 'Y', label: 'Y - Journals' },
    { id: 'Z', label: 'Z - Bilateral' },
  ];

  const accountingTypeOptions = [
    { id: 'G', label: 'Gross' },
    { id: 'N', label: 'Net' },
  ];

  // Handlers
  const handleServiceTypesClick = (event: React.MouseEvent<HTMLElement>) => {
    setServiceTypesAnchorEl(event.currentTarget);
  };

  const handleServiceTypesMenuClose = () => {
    setServiceTypesAnchorEl(null);
  };

  const handleMailFormatsClick = (event: React.MouseEvent<HTMLElement>) => {
    setMailFormatsAnchorEl(event.currentTarget);
  };

  const handleMailFormatsMenuClose = () => {
    setMailFormatsAnchorEl(null);
  };

  const handleAccountingTypesClick = (event: React.MouseEvent<HTMLElement>) => {
    setAccountingTypesAnchorEl(event.currentTarget);
  };

  const handleAccountingTypesMenuClose = () => {
    setAccountingTypesAnchorEl(null);
  };

  const handleFromPeriodClick = (event: React.MouseEvent<HTMLElement>) => {
    setFromPeriodAnchorEl(event.currentTarget);
  };

  const handleFromPeriodMenuClose = () => {
    setFromPeriodAnchorEl(null);
  };

  const handleFromPeriodChange = (period: string) => {
    customFilters.onFromPeriodChange?.(period);
    handleFromPeriodMenuClose();
  };

  const handleToPeriodClick = (event: React.MouseEvent<HTMLElement>) => {
    setToPeriodAnchorEl(event.currentTarget);
  };

  const handleToPeriodMenuClose = () => {
    setToPeriodAnchorEl(null);
  };

  const handleToPeriodChange = (period: string) => {
    customFilters.onToPeriodChange?.(period);
    handleToPeriodMenuClose();
  };

  const handleServiceTypeToggle = (value: string) => {
    const currentValues = customFilters.serviceTypes || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    customFilters.onServiceTypesChange?.(newValues);
  };

  const handleMailFormatToggle = (value: string) => {
    const currentValues = customFilters.mailFormats || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    customFilters.onMailFormatsChange?.(newValues);
  };

  const handleAccountingTypeToggle = (value: string) => {
    const currentValues = customFilters.accountingTypes || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    customFilters.onAccountingTypesChange?.(newValues);
  };

  // Display labels
  const getFromPeriodLabel = () => {
    if (!customFilters.fromPeriod) return 'Select From';
    const option = yearQuarterOptions.find(opt => opt.value === customFilters.fromPeriod);
    return option ? option.label : 'Select From';
  };

  const getToPeriodLabel = () => {
    if (!customFilters.toPeriod) return 'Select To';
    const option = yearQuarterOptions.find(opt => opt.value === customFilters.toPeriod);
    return option ? option.label : 'Select To';
  };

  const getServiceTypesLabel = () => {
    const selected = customFilters.serviceTypes || [];
    if (selected.length === 0) return 'Select Service Types';
    if (selected.length === serviceTypeOptions.length) return 'All Service Types';
    return `${selected.length} Service Types`;
  };

  const getMailFormatsLabel = () => {
    const selected = customFilters.mailFormats || [];
    if (selected.length === 0) return 'Select Mail Formats';
    if (selected.length === mailFormatOptions.length) return 'All Mail Formats';
    return `${selected.length} Mail Formats`;
  };

  const getAccountingTypesLabel = () => {
    const selected = customFilters.accountingTypes || [];
    if (selected.length === 0) return 'Select Accounting Types';
    if (selected.length === accountingTypeOptions.length) return 'All Accounting Types';
    return `${selected.length} Accounting Types`;
  };

  // E53 Status handlers
  const handleE53StatusPpoClick = (event: React.MouseEvent<HTMLElement>) => {
    setE53StatusPpoAnchorEl(event.currentTarget);
  };

  const handleE53StatusPpoMenuClose = () => {
    setE53StatusPpoAnchorEl(null);
  };

  const handleE53StatusYearClick = (event: React.MouseEvent<HTMLElement>) => {
    setE53StatusYearAnchorEl(event.currentTarget);
  };

  const handleE53StatusYearMenuClose = () => {
    setE53StatusYearAnchorEl(null);
  };

  const handleE53StatusPpoChange = (ppo: string) => {
    customFilters.onE53StatusPpoChange?.(ppo);
    handleE53StatusPpoMenuClose();
  };

  const handleE53StatusYearChange = (year: string) => {
    customFilters.onE53StatusYearChange?.(year);
    handleE53StatusYearMenuClose();
  };

  // Display labels for E53 Status
  const getE53StatusPpoLabel = () => {
    if (!customFilters.e53StatusPpo) return 'ALL';
    return customFilters.e53StatusPpo;
  };

  const getE53StatusYearLabel = () => {
    if (!customFilters.e53StatusYear) return 'ALL';
    return customFilters.e53StatusYear;
  };

  const getE53StatusDirectionLabel = () => {
    switch (customFilters.e53StatusDirection) {
      case 'inbound': return 'Inbound';
      case 'outbound': return 'Outbound';
      default: return 'All';
    }
  };

  const getE53StatusStatusLabel = () => {
    switch (customFilters.e53StatusStatus) {
      case 'open': return 'Open';
      case 'closed': return 'Closed';
      default: return 'All';
    }
  };

  return (
    <>
      {/* E53 Status specific filters - show only if E53 status props exist */}
      {customFilters.onE53StatusPpoChange && (
        <>
          {/* PPO Filter */}
          <FilterButton onClick={handleE53StatusPpoClick}>
            <span className='label'>DO</span>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                {getE53StatusPpoLabel()}
              </Typography>
              <FontAwesomeIcon icon={faChevronDown} size='sm' />
            </Box>
          </FilterButton>

          <Menu
            anchorEl={e53StatusPpoAnchorEl}
            open={isE53StatusPpoMenuOpen}
            onClose={handleE53StatusPpoMenuClose}
            PaperProps={{
              style: {
                maxHeight: 300,
                width: '200px',
              },
            }}
          >
            <MenuItem onClick={() => handleE53StatusPpoChange('')}>
              <Typography sx={{ fontWeight: 600 }}>ALL</Typography>
            </MenuItem>
            {customFilters.e53StatusPpoOptions
              ?.filter(operator => !operator.disabled)
              ?.map((operator) => (
                <MenuItem
                  key={operator.id}
                  onClick={() => handleE53StatusPpoChange(operator.name)}
                  selected={customFilters.e53StatusPpo === operator.name}
                  sx={{
                    fontWeight: customFilters.e53StatusPpo === operator.name ? 700 : 400,
                    fontSize: '14px',
                  }}
                >
                  {operator.name}
                </MenuItem>
              ))}
          </Menu>

          {/* Year Filter */}
          <FilterButton onClick={handleE53StatusYearClick}>
            <span className='label'>Year</span>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                {getE53StatusYearLabel()}
              </Typography>
              <FontAwesomeIcon icon={faChevronDown} size='sm' />
            </Box>
          </FilterButton>

          <Menu
            anchorEl={e53StatusYearAnchorEl}
            open={isE53StatusYearMenuOpen}
            onClose={handleE53StatusYearMenuClose}
            PaperProps={{
              style: {
                maxHeight: 300,
                width: '150px',
              },
            }}
          >
            <MenuItem onClick={() => handleE53StatusYearChange('')}>
              <Typography sx={{ fontWeight: 600 }}>ALL</Typography>
            </MenuItem>
            {customFilters.e53StatusYearOptions?.map((year) => (
              <MenuItem
                key={year}
                onClick={() => handleE53StatusYearChange(year)}
                selected={customFilters.e53StatusYear === year}
                sx={{
                  fontWeight: customFilters.e53StatusYear === year ? 700 : 400,
                  fontSize: '14px',
                }}
              >
                {year}
              </MenuItem>
            ))}
          </Menu>

          {/* Direction Filter */}
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
            <StyledSegmentedControl sx={{ width: '208px' }}>
              <StyledSegmentButton
                active={customFilters.e53StatusDirection === 'all'}
                onClick={() => customFilters.onE53StatusDirectionChange?.('all')}
                sx={{ color: customFilters.e53StatusDirection === 'all' ? '#ffffff' : '#00B6BA' }}
              >
                All
              </StyledSegmentButton>
              <StyledSegmentButton
                active={customFilters.e53StatusDirection === 'inbound'}
                onClick={() => customFilters.onE53StatusDirectionChange?.('inbound')}
                sx={{ color: customFilters.e53StatusDirection === 'inbound' ? '#ffffff' : '#00B6BA' }}
              >
                Inbound
              </StyledSegmentButton>
              <StyledSegmentButton
                active={customFilters.e53StatusDirection === 'outbound'}
                onClick={() => customFilters.onE53StatusDirectionChange?.('outbound')}
                sx={{ color: customFilters.e53StatusDirection === 'outbound' ? '#ffffff' : '#00B6BA' }}
              >
                Outbound
              </StyledSegmentButton>
            </StyledSegmentedControl>
          </Box>

          {/* Status Filter */}
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
            <StyledSegmentedControl sx={{ width: '168px' }}>
              <StyledSegmentButton
                active={customFilters.e53StatusStatus === 'all'}
                onClick={() => customFilters.onE53StatusStatusChange?.('all')}
                sx={{ color: customFilters.e53StatusStatus === 'all' ? '#ffffff' : '#00B6BA' }}
              >
                All
              </StyledSegmentButton>
              <StyledSegmentButton
                active={customFilters.e53StatusStatus === 'open'}
                onClick={() => customFilters.onE53StatusStatusChange?.('open')}
                sx={{ color: customFilters.e53StatusStatus === 'open' ? '#ffffff' : '#00B6BA' }}
              >
                Open
              </StyledSegmentButton>
              <StyledSegmentButton
                active={customFilters.e53StatusStatus === 'closed'}
                onClick={() => customFilters.onE53StatusStatusChange?.('closed')}
                sx={{ color: customFilters.e53StatusStatus === 'closed' ? '#ffffff' : '#00B6BA' }}
              >
                Closed
              </StyledSegmentButton>
            </StyledSegmentedControl>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
            <StyledActionButton
              onClick={() => customFilters.onE53StatusReset?.()}
              disabled={customFilters.e53StatusIsSubmitting}
              sx={{
                backgroundColor: '#f8f9fa',
                color: '#6c757d',
                border: '1px solid #dee2e6',
                '&:hover': {
                  backgroundColor: '#e9ecef',
                  borderColor: '#adb5bd',
                },
                '&:disabled': {
                  backgroundColor: '#B8C4CE',
                  color: '#6C757D',
                }
              }}
            >
              <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
                {customFilters.e53StatusIsSubmitting ? <CircularProgress size={16} color="inherit" /> : 'RESET'}
              </Typography>
            </StyledActionButton>
            <StyledActionButton
              onClick={() => customFilters.onE53StatusSubmit?.()}
              disabled={customFilters.e53StatusIsSubmitting}
              sx={{
                backgroundColor: '#6c757d',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#5a6268',
                },
                '&:disabled': {
                  backgroundColor: '#B8C4CE',
                  color: '#6C757D',
                }
              }}
            >
              <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
                {customFilters.e53StatusIsSubmitting ? <CircularProgress size={16} color="inherit" /> : 'SUBMIT'}
              </Typography>
            </StyledActionButton>
          </Box>
        </>
      )}

      {/* Original filters - show only if E53 status props don't exist */}
      {!customFilters.onE53StatusPpoChange && (
        <>
          {/* From Period Filter */}
          <FilterButton onClick={handleFromPeriodClick}>
            <span className='label'>From</span>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                {getFromPeriodLabel()}
              </Typography>
              <FontAwesomeIcon icon={faChevronDown} size='sm' />
            </Box>
          </FilterButton>

          <Menu
            anchorEl={fromPeriodAnchorEl}
            open={isFromPeriodMenuOpen}
            onClose={handleFromPeriodMenuClose}
            PaperProps={{
              style: {
                maxHeight: 300,
                width: '150px',
              },
            }}
          >
            <MenuItem onClick={() => handleFromPeriodChange('')}>
              <Typography sx={{ fontStyle: 'italic', color: '#666' }}>Clear</Typography>
            </MenuItem>
            {yearQuarterOptions.map((option) => (
              <MenuItem
                key={option.value}
                onClick={() => handleFromPeriodChange(option.value)}
                selected={customFilters.fromPeriod === option.value}
                sx={{
                  fontWeight: customFilters.fromPeriod === option.value ? 700 : 400,
                  fontSize: '14px',
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>

          {/* To Period Filter */}
          <FilterButton onClick={handleToPeriodClick}>
            <span className='label'>To</span>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                {getToPeriodLabel()}
              </Typography>
              <FontAwesomeIcon icon={faChevronDown} size='sm' />
            </Box>
          </FilterButton>

          <Menu
            anchorEl={toPeriodAnchorEl}
            open={isToPeriodMenuOpen}
            onClose={handleToPeriodMenuClose}
            PaperProps={{
              style: {
                maxHeight: 300,
                width: '150px',
              },
            }}
          >
            <MenuItem onClick={() => handleToPeriodChange('')}>
              <Typography sx={{ fontStyle: 'italic', color: '#666' }}>Clear</Typography>
            </MenuItem>
            {yearQuarterOptions.map((option) => (
              <MenuItem
                key={option.value}
                onClick={() => handleToPeriodChange(option.value)}
                selected={customFilters.toPeriod === option.value}
                sx={{
                  fontWeight: customFilters.toPeriod === option.value ? 700 : 400,
                  fontSize: '14px',
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>

          {/* Service Types Filter */}
          <FilterButton onClick={handleServiceTypesClick}>
            <span className='label'>Service Types</span>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                {getServiceTypesLabel()}
              </Typography>
              <FontAwesomeIcon icon={faChevronDown} size='sm' />
            </Box>
          </FilterButton>

          <Menu
            anchorEl={serviceTypesAnchorEl}
            open={isServiceTypesMenuOpen}
            onClose={handleServiceTypesMenuClose}
            PaperProps={{
              style: {
                maxHeight: 300,
                width: '250px',
              },
            }}
          >
            {serviceTypeOptions.map((option) => (
              <MenuItem
                key={option.id}
                onClick={() => handleServiceTypeToggle(option.id)}
                sx={{
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 16px',
                }}
              >
                <Checkbox
                  checked={customFilters.serviceTypes?.includes(option.id) || false}
                  sx={{ mr: 1, padding: '4px' }}
                />
                {option.label}
              </MenuItem>
            ))}
          </Menu>

          {/* Mail Formats Filter */}
          <FilterButton onClick={handleMailFormatsClick}>
            <span className='label'>Mail Formats</span>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                {getMailFormatsLabel()}
              </Typography>
              <FontAwesomeIcon icon={faChevronDown} size='sm' />
            </Box>
          </FilterButton>

          <Menu
            anchorEl={mailFormatsAnchorEl}
            open={isMailFormatsMenuOpen}
            onClose={handleMailFormatsMenuClose}
            PaperProps={{
              style: {
                maxHeight: 300,
                width: '200px',
              },
            }}
          >
            {mailFormatOptions.map((option) => (
              <MenuItem
                key={option.id}
                onClick={() => handleMailFormatToggle(option.id)}
                sx={{
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 16px',
                }}
              >
                <Checkbox
                  checked={customFilters.mailFormats?.includes(option.id) || false}
                  sx={{ mr: 1, padding: '4px' }}
                />
                {option.label}
              </MenuItem>
            ))}
          </Menu>

          {/* Accounting Types Filter */}
          <FilterButton onClick={handleAccountingTypesClick}>
            <span className='label'>Accounting Types</span>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                {getAccountingTypesLabel()}
              </Typography>
              <FontAwesomeIcon icon={faChevronDown} size='sm' />
            </Box>
          </FilterButton>

          <Menu
            anchorEl={accountingTypesAnchorEl}
            open={isAccountingTypesMenuOpen}
            onClose={handleAccountingTypesMenuClose}
            PaperProps={{
              style: {
                maxHeight: 300,
                width: '180px',
              },
            }}
          >
            {accountingTypeOptions.map((option) => (
              <MenuItem
                key={option.id}
                onClick={() => handleAccountingTypeToggle(option.id)}
                sx={{
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 16px',
                }}
              >
                <Checkbox
                  checked={customFilters.accountingTypes?.includes(option.id) || false}
                  sx={{ mr: 1, padding: '4px' }}
                />
                {option.label}
              </MenuItem>
            ))}
          </Menu>

          {/* Export Buttons */}
          <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
            <StyledActionButton
              onClick={() => customFilters.onExport?.('HTML')}
              disabled={customFilters.isExporting}
              sx={{
                backgroundColor: '#00ACE7',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#0095C7',
                },
                '&:disabled': {
                  backgroundColor: '#B8C4CE',
                  color: '#6C757D',
                }
              }}
            >
              <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
                {customFilters.isExporting ? <CircularProgress size={16} color="inherit" /> : 'HTML'}
              </Typography>
            </StyledActionButton>
            <StyledActionButton
              onClick={() => customFilters.onExport?.('PDF')}
              disabled={customFilters.isExporting}
              sx={{
                backgroundColor: '#00ACE7',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#0095C7',
                },
                '&:disabled': {
                  backgroundColor: '#B8C4CE',
                  color: '#6C757D',
                }
              }}
            >
              <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
                {customFilters.isExporting ? <CircularProgress size={16} color="inherit" /> : 'PDF'}
              </Typography>
            </StyledActionButton>
            <StyledActionButton
              onClick={() => customFilters.onExport?.('XLS')}
              disabled={customFilters.isExporting}
              sx={{
                backgroundColor: '#00ACE7',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#0095C7',
                },
                '&:disabled': {
                  backgroundColor: '#B8C4CE',
                  color: '#6C757D',
                }
              }}
            >
              <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
                {customFilters.isExporting ? <CircularProgress size={16} color="inherit" /> : 'XLS'}
              </Typography>
            </StyledActionButton>
          </Box>
        </>
      )}
    </>
  );
};

export default E53StatusFilters; 